import {CSSProperties} from 'react'
import {Theme} from '@mui/material'
import {createTheme, alpha, ThemeOptions} from '@mui/material/styles'
import {syskronPalette, SyskronPalette} from './SyskronPalette'
import {deepmerge} from '@mui/utils'

const defaultFontFamilies = [
    'Noto Sans',
    'Noto Sans SC',
    'Noto Sans CJK',
    'Noto Sans CJK SC',
    '思源黑體',
    'Droid Sans',
    'Phetsarath',
    'Droid Sans',
    'Trebuchet MS',
    'Microsoft YaHei',
    '微软雅黑',
    'PingFang SC',
    'sans-serif',
    'Noto Color Emoji',
    'Segoe UI Emoji',
    'Segoe UI Symbol',
    'Apple Color Emoji'
].join(',')

export interface OcTheme extends Theme {
    syskronPalette: SyskronPalette
}

export function getHoverStyle(color?: string, backgroundColor?: string, otherProps?: CSSProperties) {
    return {
        '&:hover': {
            ...otherProps,
            color: color ? color : 'default',
            backgroundColor: backgroundColor ? backgroundColor : 'transparent'
        }
    }
}

export function getScrollStyle(indicatorColor?: string, backgroundColor?: string, scrollWidth?: number) {
    const background = backgroundColor || syskronPalette.colors.S100
    return {
        '&::-webkit-scrollbar': {
            width: scrollWidth || 16,
            backgroundColor: background
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: indicatorColor || syskronPalette.colors.S400,
            border: `solid ${background}`,
            borderWidth: `0px 0px 0px ${(scrollWidth || 16) / 2}px`
        }
    }
}

function getSelectionStyle() {
    return {
        '&::selection': {
            color: syskronPalette.common.textBlue,
            backgroundColor: syskronPalette.colors.P200
        }
    }
}

function checkRadioStyle(colorBase: string, colorChecked: string) {
    return {
        color: colorBase,
        '&.Mui-checked.Mui-disabled': {
            color: syskronPalette.colors.P300
        },
        '&.Mui-checked': {
            color: colorChecked
        }
    }
}

function switchStyle(
    colorButton: string,
    colorTrack: string,
    colorButtonChecked: string,
    colorTrackChecked: string,
    colorTrackDisabled: string,
    colorCheckedDisabled: string
) {
    return {
        color: colorButton,
        '&.Mui-checked.Mui-disabled  + .MuiSwitch-track': {
            backgroundColor: colorCheckedDisabled,
            opacity: 1
        },
        '&.Mui-disabled  + .MuiSwitch-track': {
            backgroundColor: colorTrackDisabled,
            opacity: 1
        },
        '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: colorTrackChecked,
            opacity: 1
        },
        '& + .MuiSwitch-track': {
            backgroundColor: colorTrack,
            opacity: 1
        },
        '&.Mui-checked.Mui-disabled': {
            color: colorCheckedDisabled
        },
        '&.Mui-disabled': {
            color: colorTrack
        },
        '&.Mui-checked': {
            color: colorButtonChecked
        }
    }
}

const textTransformNoneProps = {
    textTransform: 'none'
} as const

const buttonTypoProps = {
    fontWeight: 400,
    fontSize: '14px',
    fontStyle: 'normal',
    lineHeight: 'normal',
    ...textTransformNoneProps
} as const

const body2TypoProps = {
    fontWeight: 400,
    lineHeight: '18px',
    fontSize: '14px',
    fontStyle: 'normal',
    ...textTransformNoneProps
} as const

/** Properties needed for Typography variant from figma 'button2' - MUI does not have this variant, can be combined with variant 'button' via CSS styling.*/
export const ocTypohraphyVariantButton2Props = {
    fontWeight: 500,
    lineHeight: '18px' /* 128.571% */,
    textTransform: 'uppercase'
} as const

export const ocTheme = createTheme(
    deepmerge(
        {
            palette: {
                primary: {
                    main: syskronPalette.colors.P500, // texfield border active, most component colors
                    dark: syskronPalette.colors.P700, // button hover
                    light: alpha(syskronPalette.colors.P500, 0.1),
                    contrastText: syskronPalette.contrastColors.P500 // text for buttons
                },
                secondary: {
                    main: syskronPalette.colors.P100, // texfield border active, most component colors
                    dark: syskronPalette.colors.P200, // button hover
                    light: alpha(syskronPalette.colors.P100, 0.1),
                    contrastText: syskronPalette.contrastColors.P100 // text for buttons
                },
                text: {
                    primary: syskronPalette.colors.S700, // typography, input texts,
                    secondary: syskronPalette.colors.P700, // empty checkbox, textfield label
                    disabled: syskronPalette.colors.S400
                },
                background: {
                    default: syskronPalette.colors.S100,
                    paper: syskronPalette.colors.P50
                },
                success: {
                    main: syskronPalette.common.green,
                    contrastText: syskronPalette.common.greenContrast
                },
                warning: {
                    main: syskronPalette.common.yellow,
                    contrastText: syskronPalette.common.yellowContrast
                },
                error: {
                    main: syskronPalette.common.red,
                    contrastText: syskronPalette.common.redContrast
                },
                action: {
                    active: syskronPalette.colors.P700, // IconButton and other actions (cliclable items)
                    hoverOpacity: 0.1, // somehow used for counting hover color, e.g. for button
                    disabled: syskronPalette.colors.S400,
                    hover: syskronPalette.common.itemHover,
                    focus: syskronPalette.common.itemSelected,
                    focusOpacity: 0.1
                },
                divider: alpha(syskronPalette.colors.S600, 0.4)
            },
            shape: {
                borderRadius: 0
            },
            typography: {
                htmlFontSize: 16,
                fontFamily: defaultFontFamilies,
                fontSize: 16,
                fontWeightLight: 300,
                fontWeightRegular: 400,
                fontWeightMedium: 500,
                fontWeightBold: 700,
                h1: {
                    fontFamily: defaultFontFamilies,
                    fontWeight: 500,
                    fontSize: '24px',
                    fontStyle: 'normal',
                    lineHeight: '32px',
                    color: syskronPalette.colors.S900,
                    ...textTransformNoneProps
                },
                h2: {
                    fontFamily: defaultFontFamilies,
                    fontWeight: 500,
                    fontSize: '19px',
                    lineHeight: '24px',
                    letterSpacing: '-0.19px',
                    fontStyle: 'normal',
                    color: syskronPalette.colors.S900,
                    ...textTransformNoneProps
                },
                h3: {
                    fontFamily: defaultFontFamilies,
                    fontWeight: 700,
                    fontSize: '19px',
                    lineHeight: '24px',
                    fontStyle: 'normal',
                    color: syskronPalette.colors.S900,
                    ...textTransformNoneProps
                },
                h4: {
                    fontFamily: defaultFontFamilies,
                    fontWeight: 700,
                    fontStyle: 'normal',
                    lineHeight: '22px',
                    fontSize: '16px',
                    letterSpacing: 0.15,
                    color: syskronPalette.colors.S900,
                    ...textTransformNoneProps
                },
                h5: {
                    fontFamily: defaultFontFamilies,
                    fontWeight: 700,
                    fontSize: '14px',
                    lineHeight: '18px',
                    fontStyle: 'normal',
                    ...textTransformNoneProps
                },
                h6: {
                    fontFamily: defaultFontFamilies,
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    lineHeight: 1.286,
                    letterSpacing: '0rem',
                    ...textTransformNoneProps
                },
                subtitle1: {
                    fontFamily: defaultFontFamilies,
                    fontWeight: 700,
                    fontSize: '12px',
                    lineHeight: '16px',
                    fontStyle: 'normal',
                    ...textTransformNoneProps
                },
                subtitle2: {
                    fontFamily: defaultFontFamilies,
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '16px',
                    fontStyle: 'normal',
                    ...textTransformNoneProps
                },
                body1: {
                    fontFamily: defaultFontFamilies,
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '22px',
                    fontStyle: 'normal',
                    ...textTransformNoneProps
                },
                body2: {
                    fontFamily: defaultFontFamilies,
                    ...body2TypoProps
                },
                button: {
                    fontFamily: defaultFontFamilies,
                    ...buttonTypoProps
                },
                caption: {
                    fontFamily: defaultFontFamilies,
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: 'normal',
                    fontStyle: 'normal',
                    ...textTransformNoneProps
                },
                overline: {
                    fontFamily: defaultFontFamilies,
                    fontWeight: 700,
                    fontSize: '20px',
                    lineHeight: '20px',
                    fontStyle: 'normal',
                    textTransform: 'uppercase'
                }
            },
            components: {
                MuiButton: {
                    styleOverrides: {
                        root: {
                            padding: '10px 16px 11px 16px'
                        },
                        text: {
                            padding: '6px 8px'
                        },
                        outlined: {
                            padding: '5px 15px'
                        },
                        textSecondary: {
                            color: syskronPalette.common.textBlue,
                            ...getHoverStyle(
                                syskronPalette.common.textBlue,
                                alpha(syskronPalette.common.textBlue, 0.1).toString(),
                                {borderColor: syskronPalette.common.textBlue}
                            )
                        },
                        outlinedSecondary: {
                            color: syskronPalette.common.textBlue,
                            borderColor: syskronPalette.common.textBlue,
                            ...getHoverStyle(
                                syskronPalette.common.textBlue,
                                alpha(syskronPalette.common.textBlue, 0.1).toString(),
                                {borderColor: syskronPalette.common.textBlue}
                            )
                        }
                    }
                },
                MuiTypography: {
                    styleOverrides: {
                        root: {
                            ...getSelectionStyle()
                        }
                    }
                },
                MuiCheckbox: {
                    styleOverrides: {
                        colorPrimary: {
                            ...checkRadioStyle(syskronPalette.common.textBlue, syskronPalette.common.textLightBlue)
                        }
                    }
                },
                MuiRadio: {
                    styleOverrides: {
                        colorPrimary: {
                            ...checkRadioStyle(syskronPalette.common.textBlue, syskronPalette.common.textLightBlue)
                        }
                    }
                },
                MuiSwitch: {
                    styleOverrides: {
                        colorPrimary: {
                            ...switchStyle(
                                syskronPalette.colors.P700, //colorButton
                                syskronPalette.colors.S400, //colorTrack
                                syskronPalette.colors.P500, //colorButtonChecked
                                syskronPalette.colors.P400, //colorTrackChecked
                                syskronPalette.colors.S300, //colorTrackDisabled
                                syskronPalette.colors.P300 //colorCheckedDisabled
                            )
                        }
                    }
                },
                MuiSlider: {
                    styleOverrides: {
                        colorSecondary: {
                            color: syskronPalette.colors.P700
                        }
                    },
                    defaultProps: {
                        size: 'small'
                    }
                },
                MuiTabs: {
                    styleOverrides: {
                        indicator: {
                            backgroundColor: syskronPalette.common.textLightBlue
                        },
                        root: {
                            minHeight: 0,
                            paddingTop: 2
                        },
                        scroller: {
                            paddingBottom: 1
                        }
                    }
                },
                MuiTab: {
                    styleOverrides: {
                        // ze staryho theme
                        wrapped: {
                            ...buttonTypoProps,
                            display: 'flex',
                            flexDirection: 'row',
                            // Button2 Figma variant
                            ...ocTypohraphyVariantButton2Props
                        },
                        root: {
                            textTransform: 'uppercase',
                            padding: '6px 12px',
                            minHeight: 0, // jenom tohle bylo ve starym theme
                            minWidth: 160
                        },
                        labelIcon: {
                            minHeight: 0,
                            // otestovat ze .MuiTab-icon existuje v labelIcon
                            '& > .MuiTab-icon > *:first-of-type': {
                                marginBottom: '0px'
                            }
                        }
                    },
                    defaultProps: {
                        iconPosition: 'start'
                    }
                },
                MuiTooltip: {
                    styleOverrides: {
                        tooltip: {
                            ...body2TypoProps
                        }
                    }
                },
                MuiLink: {
                    styleOverrides: {
                        underlineNone: {
                            cursor: 'pointer',
                            color: syskronPalette.common.textLightBlue,
                            ...getHoverStyle(syskronPalette.common.textBlue, 'default')
                        }
                    },
                    defaultProps: {
                        underline: 'hover'
                    }
                },
                MuiTextField: {
                    styleOverrides: {
                        root: {
                            '&:has(div[class*=MuiOutlinedInput-root])': {
                                marginTop: '9px'
                            }
                        }
                    },
                    defaultProps: {
                        variant: 'standard'
                    }
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            color: syskronPalette.common.textBlue,
                            '&.Mui-error.Mui-focused': {
                                color: syskronPalette.common.textBlue
                            },
                            '&.Mui-error.Mui-focused > .MuiOutlinedInput-notchedOutline': {
                                borderColor: syskronPalette.common.textLightBlue
                            },
                            '&:hover > .MuiOutlinedInput-notchedOutline': {
                                borderColor: syskronPalette.common.textDarkBlue,
                                border: '2px solid'
                            },
                            '&.Mui-focused:hover > .MuiOutlinedInput-notchedOutline': {
                                borderColor: syskronPalette.common.textLightBlue
                            },
                            '&.Mui-error.Mui-focused:hover > .MuiOutlinedInput-notchedOutline': {
                                borderColor: syskronPalette.common.textLightBlue
                            },
                            '&.Mui-disabled:hover > .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'inherit',
                                border: '1px solid'
                            },
                            '&.MuiInputBase-multiline > .MuiInputBase-input': {
                                padding: 0,
                                lineHeight: '19px'
                            },
                            '&.MuiInputBase-adornedStart > .MuiInputBase-input': {
                                paddingLeft: 0
                            }
                        },
                        input: {
                            padding: '13px',
                            //height: '1.1876em',
                            backgroundColor: syskronPalette.colors.P50,
                            ...getSelectionStyle(),
                            '&::placeholder': {
                                color: syskronPalette.colors.P200,
                                opacity: 1
                            }
                        },
                        notchedOutline: {
                            borderColor: syskronPalette.common.textBlue
                        },
                        multiline: {
                            padding: '18.5px 14px'
                        }
                    }
                },
                MuiFormHelperText: {
                    styleOverrides: {
                        root: {
                            color: syskronPalette.common.textGrey,
                            '&.Mui-error': {
                                color: syskronPalette.common.red
                            }
                        }
                    }
                },
                MuiFilledInput: {
                    styleOverrides: {
                        input: {
                            paddingTop: 28
                        }
                    }
                },
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            lineHeight: '1em',
                            overflow: 'visible'
                        },
                        shrink: {
                            lineHeight: '1.4375em'
                        },
                        outlined: {
                            color: syskronPalette.common.textGrey,
                            //transform: 'translate(14px, 15px) scale(1)',
                            '&.Mui-error': {
                                color: syskronPalette.common.textGrey
                            },
                            '&.Mui-error.Mui-focused': {
                                color: syskronPalette.common.red
                            },
                            '&.Mui-focused': {
                                color: syskronPalette.common.textLightBlue
                            }
                        }
                    }
                },
                MuiChip: {
                    styleOverrides: {
                        root: {
                            '& > .MuiChip-label': {
                                ...getSelectionStyle()
                            }
                        },
                        colorDefault: {
                            color: syskronPalette.colors.S900,
                            background: syskronPalette.colors.S200
                        }
                    }
                },
                MuiFormControlLabel: {
                    styleOverrides: {
                        root: {
                            '&.Mui-disabled': {
                                color: syskronPalette.colors.S400
                            }
                        }
                    }
                },
                MuiInputBase: {
                    styleOverrides: {
                        input: {
                            height: '1.1876em'
                        }
                    }
                },
                // TOMAS check all autocomplete selects
                // TOMAS check if we can't replace OcAutocomplete with MUI's autocomplete
                MuiAutocomplete: {
                    styleOverrides: {
                        root: {
                            '& .MuiOutlinedInput-root': {
                                padding: '5.5px 8px'
                            }
                        }
                    }
                }
            }
        } satisfies ThemeOptions,
        {syskronPalette}
    )
) as OcTheme
